import React, { ReactElement, Dispatch } from 'react';
import { Button } from '@ui/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { togglerTabButtonClass } from '@app/components/core/toggler-tab-button-class';
import { runViewTransition } from '@app/components/core/viewTransition';

interface TogglerProps<T> {
  tabs: T[];
  activeTab: T;
  setActiveTab: Dispatch<T>;
}

export const GenericToggler = <T,>({ tabs, activeTab, setActiveTab }: TogglerProps<T>): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const setTab = (tab: T) => {
    if (tab === activeTab) return;
    // Cross-fade the new view in, the same quick beat as the other same-level switches (see the `fade`
    // rules in globals.css). No swipe: a toggle keeps you where you are, it doesn't go a layer deeper.
    runViewTransition('fade', () => {
      setActiveTab(tab);
      // A tab is view state, not a navigation step. Pushing an entry per click left the detail pages'
      // back control (useBackNavigation pops history) walking back through tab changes instead of leaving.
      navigate({ search: `?tab=${tab}` }, { replace: true });
    });
  };

  return (
    <div
      className="no-scrollbar inline-flex max-w-full items-stretch overflow-x-auto rounded-md bg-card p-1"
      data-slot="button-group"
      role="group"
    >
      {tabs.map(tab => (
        <Button key={tab as string} className={togglerTabButtonClass(activeTab === tab)} onClick={() => setTab(tab)}>
          {t(`tabs.${tab}`)}
        </Button>
      ))}
    </div>
  );
};
